import { useState } from 'react';
import { useNotify } from 'react-admin';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Button, Typography } from '@mui/material';
import axios from 'axios';

const API_URL = window.ENV.API_URL ?? 'http://localhost:8080/api/v1';

interface TaxonomyPartnerHeroImageProps {
  taxonomyId?: number;
}

export const TaxonomyPartnerHeroImage = ({ taxonomyId }: TaxonomyPartnerHeroImageProps) => {
  const notify = useNotify();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedFile || !taxonomyId) {
      notify('Please select a file first', { type: 'warning' });
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('hero_image', selectedFile);

      await axios.put(`${API_URL}/partner-taxonomies/${taxonomyId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });

      notify('Image uploaded successfully', { type: 'success' });
      setSelectedFile(null);
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error: any) {
      notify('Failed to upload image', { type: 'error' });
      console.error('Error uploading image:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageDelete = async () => {
    if (!taxonomyId) {
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('clear_hero_image', 'true');

      await axios.put(`${API_URL}/partner-taxonomies/${taxonomyId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });

      notify('Hero image deleted successfully', { type: 'success' });
    } catch (error: any) {
      notify('Failed to delete image', { type: 'error' });
      console.error('Error deleting image:', error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box sx={{ border: '1px solid #ccc', borderRadius: '4px', padding: '16px', marginTop: '16px' }}>
      <Typography variant="subtitle1" sx={{ marginBottom: '12px', fontWeight: 'bold' }}>
        Upload Partner Hero Image
      </Typography>
      <Box sx={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
        <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} disabled={isUploading}>
          Choose Image
          <input type="file" accept="image/*" hidden onChange={handleFileChange} />
        </Button>
        {selectedFile && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {selectedFile.name}
          </Typography>
        )}
        {selectedFile && (
          <Button variant="contained" onClick={handleImageUpload} disabled={isUploading}>
            {isUploading ? 'Uploading...' : 'Save Image'}
          </Button>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          flexDirection: 'column',
          marginTop: '24px',
          paddingTop: '16px',
          borderTop: '1px solid #eee',
        }}>
        <Typography variant="subtitle1" sx={{ marginBottom: '12px', fontWeight: 'bold' }}>
          Delete Partner Hero Image
        </Typography>
        <Button variant="outlined" color="error" onClick={handleImageDelete} disabled={isUploading}>
          Delete Current Hero Image
        </Button>
      </Box>
    </Box>
  );
};
